import {createSlice, PayloadAction} from "@reduxjs/toolkit";

import {RootState} from "../../../store/types/RootState";
import {UserAmplitudePhoneVerifiedLocation} from "../../tracking/amplitude/user_amplitude_tracker";
import {PhoneVerificationFlowStatusType} from "../interfaces/PhoneVerificationFlowStatusType";
import {
    IPhoneVerificationFlowOutcome,
    IRequestPhoneVerificationPayload,
    PHONE_VERIFICATION_PENDING_ACTION_NONE,
    PhoneVerificationFlowOutcomeKind,
    PhoneVerificationPendingActionType
} from "../phone_verification/types/phone_verification_types";

export interface UserPhoneVerificationStore {
    isPhoneVerificationModalSuppressed: boolean;
    openPhoneVerificationModal: boolean;
    openUpdatePhoneNumberModal: boolean;
    registrationPhoneNumberModalLocation: UserAmplitudePhoneVerifiedLocation | null;
    flowStatus: PhoneVerificationFlowStatusType;
    pendingAction: IRequestPhoneVerificationPayload["pendingAction"];
    awaitingCompletionActionType: IRequestPhoneVerificationPayload["pendingAction"]["type"] | null;
    lastFlowOutcome: IPhoneVerificationFlowOutcome | null;
}

const initialState: UserPhoneVerificationStore = {
    isPhoneVerificationModalSuppressed: false,
    openPhoneVerificationModal: false,
    openUpdatePhoneNumberModal: false,
    registrationPhoneNumberModalLocation: null,
    flowStatus: PhoneVerificationFlowStatusType.UNINITIALIZED,
    pendingAction: PHONE_VERIFICATION_PENDING_ACTION_NONE,
    awaitingCompletionActionType: null,
    lastFlowOutcome: null
};

export const userPhoneVerificationSlice = createSlice({
    name: "userPhoneVerification",
    initialState,
    reducers: {
        requestPhoneVerification: (state, action: PayloadAction<IRequestPhoneVerificationPayload>) => {
            const {pendingAction, awaitCompletion} = action.payload;

            if (state.isPhoneVerificationModalSuppressed) {
                // Another flow (the preferences wizard) owns phone verification now, so nobody could ever show this request.
                if (awaitCompletion) state.lastFlowOutcome = {kind: PhoneVerificationFlowOutcomeKind.DISMISSED, type: pendingAction.type};

                return;
            }

            state.openPhoneVerificationModal = true;
            state.registrationPhoneNumberModalLocation = null;
            state.pendingAction = pendingAction;
            state.flowStatus = PhoneVerificationFlowStatusType.INITIALIZED;
            state.lastFlowOutcome = null;
            state.awaitingCompletionActionType = awaitCompletion ? pendingAction.type : null;
        },
        reopenPhoneVerificationModal: (state) => {
            if (state.isPhoneVerificationModalSuppressed) return;

            state.openPhoneVerificationModal = true;
            state.flowStatus = PhoneVerificationFlowStatusType.INITIALIZED;
        },
        setPhoneVerificationModalSuppressed: (state, action: PayloadAction<boolean>) => {
            state.isPhoneVerificationModalSuppressed = action.payload;

            if (action.payload) {
                // A request that is still open cannot be finished behind the wizard, so end it like a dismissed modal.
                if (state.pendingAction.type !== PhoneVerificationPendingActionType.NONE || state.awaitingCompletionActionType !== null) {
                    state.lastFlowOutcome = {kind: PhoneVerificationFlowOutcomeKind.DISMISSED, type: state.pendingAction.type};
                }

                state.openPhoneVerificationModal = false;
                state.openUpdatePhoneNumberModal = false;
                state.registrationPhoneNumberModalLocation = null;
                state.pendingAction = PHONE_VERIFICATION_PENDING_ACTION_NONE;
                state.awaitingCompletionActionType = null;
                state.flowStatus = PhoneVerificationFlowStatusType.UNINITIALIZED;
            }
        },
        phoneVerificationSucceeded: (state) => {
            state.openPhoneVerificationModal = false;
            state.flowStatus = PhoneVerificationFlowStatusType.SUCCESS;
        },
        phoneVerificationCancelled: (state) => {
            state.lastFlowOutcome = {
                kind: PhoneVerificationFlowOutcomeKind.DISMISSED,
                type: state.pendingAction.type
            };
            state.openPhoneVerificationModal = false;
            state.openUpdatePhoneNumberModal = false;
            state.registrationPhoneNumberModalLocation = null;
            state.pendingAction = PHONE_VERIFICATION_PENDING_ACTION_NONE;
            state.awaitingCompletionActionType = null;
            state.flowStatus = PhoneVerificationFlowStatusType.UNINITIALIZED;
        },
        completePendingAction: (state, action: PayloadAction<{success: boolean}>) => {
            state.lastFlowOutcome = {
                kind: PhoneVerificationFlowOutcomeKind.COMPLETED,
                type: state.pendingAction.type,
                success: action.payload.success
            };

            state.openPhoneVerificationModal = false;
            state.openUpdatePhoneNumberModal = false;
            state.registrationPhoneNumberModalLocation = null;
            state.pendingAction = PHONE_VERIFICATION_PENDING_ACTION_NONE;
            state.awaitingCompletionActionType = null;
            state.flowStatus = PhoneVerificationFlowStatusType.UNINITIALIZED;
        },
        resetLastFlowOutcome: (state) => {
            state.lastFlowOutcome = null;
        },
        clearAwaitingPhoneVerificationCompletion: (state) => {
            state.awaitingCompletionActionType = null;
        },
        handleOpenRegistrationPhoneNumberModal: (state, action: PayloadAction<UserAmplitudePhoneVerifiedLocation>) => {
            if (state.isPhoneVerificationModalSuppressed) return;

            state.registrationPhoneNumberModalLocation = action.payload;
        },
        handleCloseRegistrationPhoneNumberModal: (state) => {
            state.registrationPhoneNumberModalLocation = null;
        },
        handleOpenUpdatePhoneNumberModal: (state) => {
            if (state.isPhoneVerificationModalSuppressed) return;

            state.openPhoneVerificationModal = false;
            state.openUpdatePhoneNumberModal = true;
        },
        handleCloseUpdatePhoneNumberModal: (state) => {
            state.openUpdatePhoneNumberModal = false;
        }
    }
});

export const {
    requestPhoneVerification,
    reopenPhoneVerificationModal,
    setPhoneVerificationModalSuppressed,
    phoneVerificationSucceeded,
    phoneVerificationCancelled,
    completePendingAction,
    resetLastFlowOutcome,
    clearAwaitingPhoneVerificationCompletion,
    handleOpenRegistrationPhoneNumberModal,
    handleCloseRegistrationPhoneNumberModal,
    handleOpenUpdatePhoneNumberModal,
    handleCloseUpdatePhoneNumberModal
} = userPhoneVerificationSlice.actions;

export const selectPhoneVerificationFlowStatus = (state: RootState) => state.userPhoneVerification.flowStatus;
export const selectPendingAction = (state: RootState) => state.userPhoneVerification.pendingAction;
export const selectAwaitingCompletionActionType = (state: RootState) => state.userPhoneVerification.awaitingCompletionActionType;
export const selectLastFlowOutcome = (state: RootState) => state.userPhoneVerification.lastFlowOutcome;
export const selectRegistrationPhoneNumberModalLocation = (state: RootState) => state.userPhoneVerification.registrationPhoneNumberModalLocation;
export const selectOpenRegistrationPhoneNumberModal = (state: RootState) => state.userPhoneVerification.registrationPhoneNumberModalLocation !== null;
import {createSlice, PayloadAction} from "@reduxjs/toolkit";

import {RootState} from "../../../store/types/RootState";
import {UserAmplitudePhoneVerifiedLocation} from "../../tracking/amplitude/user_amplitude_tracker";
import {PhoneVerificationFlowStatusType} from "../interfaces/PhoneVerificationFlowStatusType";
import {
    IPhoneVerificationFlowOutcome,
    IRequestPhoneVerificationPayload,
    PHONE_VERIFICATION_PENDING_ACTION_NONE,
    PhoneVerificationFlowOutcomeKind,
    PhoneVerificationPendingActionType
} from "../phone_verification/types/phone_verification_types";

export interface UserPhoneVerificationStore {
    isPhoneVerificationModalSuppressed: boolean;
    openPhoneVerificationModal: boolean;
    openUpdatePhoneNumberModal: boolean;
    registrationPhoneNumberModalLocation: UserAmplitudePhoneVerifiedLocation | null;
    flowStatus: PhoneVerificationFlowStatusType;
    pendingAction: IRequestPhoneVerificationPayload["pendingAction"];
    awaitingCompletionActionType: IRequestPhoneVerificationPayload["pendingAction"]["type"] | null;
    lastFlowOutcome: IPhoneVerificationFlowOutcome | null;
}

const initialState: UserPhoneVerificationStore = {
    isPhoneVerificationModalSuppressed: false,
    openPhoneVerificationModal: false,
    openUpdatePhoneNumberModal: false,
    registrationPhoneNumberModalLocation: null,
    flowStatus: PhoneVerificationFlowStatusType.UNINITIALIZED,
    pendingAction: PHONE_VERIFICATION_PENDING_ACTION_NONE,
    awaitingCompletionActionType: null,
    lastFlowOutcome: null
};

export const userPhoneVerificationSlice = createSlice({
    name: "userPhoneVerification",
    initialState,
    reducers: {
        requestPhoneVerification: (state, action: PayloadAction<IRequestPhoneVerificationPayload>) => {
            const {pendingAction, awaitCompletion} = action.payload;

            if (state.isPhoneVerificationModalSuppressed) {
                // Another flow (the preferences wizard) owns phone verification now, so nobody could ever show this request.
                if (awaitCompletion) state.lastFlowOutcome = {kind: PhoneVerificationFlowOutcomeKind.DISMISSED, type: pendingAction.type};

                return;
            }

            state.openPhoneVerificationModal = true;
            state.registrationPhoneNumberModalLocation = null;
            state.pendingAction = pendingAction;
            state.flowStatus = PhoneVerificationFlowStatusType.INITIALIZED;
            state.lastFlowOutcome = null;
            state.awaitingCompletionActionType = awaitCompletion ? pendingAction.type : null;
        },
        reopenPhoneVerificationModal: (state) => {
            if (state.isPhoneVerificationModalSuppressed) return;

            state.openPhoneVerificationModal = true;
            state.flowStatus = PhoneVerificationFlowStatusType.INITIALIZED;
        },
        setPhoneVerificationModalSuppressed: (state, action: PayloadAction<boolean>) => {
            state.isPhoneVerificationModalSuppressed = action.payload;

            if (action.payload) {
                // A request that is still open cannot be finished behind the wizard, so end it like a dismissed modal.
                if (state.pendingAction.type !== PhoneVerificationPendingActionType.NONE || state.awaitingCompletionActionType !== null) {
                    state.lastFlowOutcome = {kind: PhoneVerificationFlowOutcomeKind.DISMISSED, type: state.pendingAction.type};
                }

                state.openPhoneVerificationModal = false;
                state.openUpdatePhoneNumberModal = false;
                state.registrationPhoneNumberModalLocation = null;
                state.pendingAction = PHONE_VERIFICATION_PENDING_ACTION_NONE;
                state.awaitingCompletionActionType = null;
                state.flowStatus = PhoneVerificationFlowStatusType.UNINITIALIZED;
            }
        },
        phoneVerificationSucceeded: (state) => {
            state.openPhoneVerificationModal = false;
            state.flowStatus = PhoneVerificationFlowStatusType.SUCCESS;
        },
        phoneVerificationCancelled: (state) => {
            state.lastFlowOutcome = {
                kind: PhoneVerificationFlowOutcomeKind.DISMISSED,
                type: state.pendingAction.type
            };
            state.openPhoneVerificationModal = false;
            state.openUpdatePhoneNumberModal = false;
            state.registrationPhoneNumberModalLocation = null;
            state.pendingAction = PHONE_VERIFICATION_PENDING_ACTION_NONE;
            state.awaitingCompletionActionType = null;
            state.flowStatus = PhoneVerificationFlowStatusType.UNINITIALIZED;
        },
        completePendingAction: (state, action: PayloadAction<{success: boolean}>) => {
            state.lastFlowOutcome = {
                kind: PhoneVerificationFlowOutcomeKind.COMPLETED,
                type: state.pendingAction.type,
                success: action.payload.success
            };

            state.openPhoneVerificationModal = false;
            state.openUpdatePhoneNumberModal = false;
            state.registrationPhoneNumberModalLocation = null;
            state.pendingAction = PHONE_VERIFICATION_PENDING_ACTION_NONE;
            state.awaitingCompletionActionType = null;
            state.flowStatus = PhoneVerificationFlowStatusType.UNINITIALIZED;
        },
        resetLastFlowOutcome: (state) => {
            state.lastFlowOutcome = null;
        },
        clearAwaitingPhoneVerificationCompletion: (state) => {
            state.awaitingCompletionActionType = null;
        },
        handleOpenRegistrationPhoneNumberModal: (state, action: PayloadAction<UserAmplitudePhoneVerifiedLocation>) => {
            if (state.isPhoneVerificationModalSuppressed) return;

            state.registrationPhoneNumberModalLocation = action.payload;
        },
        handleCloseRegistrationPhoneNumberModal: (state) => {
            state.registrationPhoneNumberModalLocation = null;
        },
        handleOpenUpdatePhoneNumberModal: (state) => {
            if (state.isPhoneVerificationModalSuppressed) return;

            state.openPhoneVerificationModal = false;
            state.openUpdatePhoneNumberModal = true;
        },
        handleCloseUpdatePhoneNumberModal: (state) => {
            state.openUpdatePhoneNumberModal = false;
        }
    }
});

export const {
    requestPhoneVerification,
    reopenPhoneVerificationModal,
    setPhoneVerificationModalSuppressed,
    phoneVerificationSucceeded,
    phoneVerificationCancelled,
    completePendingAction,
    resetLastFlowOutcome,
    clearAwaitingPhoneVerificationCompletion,
    handleOpenRegistrationPhoneNumberModal,
    handleCloseRegistrationPhoneNumberModal,
    handleOpenUpdatePhoneNumberModal,
    handleCloseUpdatePhoneNumberModal
} = userPhoneVerificationSlice.actions;

export const selectPhoneVerificationFlowStatus = (state: RootState) => state.userPhoneVerification.flowStatus;
export const selectPendingAction = (state: RootState) => state.userPhoneVerification.pendingAction;
export const selectAwaitingCompletionActionType = (state: RootState) => state.userPhoneVerification.awaitingCompletionActionType;
export const selectLastFlowOutcome = (state: RootState) => state.userPhoneVerification.lastFlowOutcome;
export const selectRegistrationPhoneNumberModalLocation = (state: RootState) => state.userPhoneVerification.registrationPhoneNumberModalLocation;
export const selectOpenRegistrationPhoneNumberModal = (state: RootState) => state.userPhoneVerification.registrationPhoneNumberModalLocation !== null;
